import type { Candidate, Timesheet, TimesheetListItem } from '@tabel/shared/schemas/timesheets';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, Copy, Search, UserPlus } from 'lucide-react';
import { type KeyboardEvent, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { type CopyRowsPreview, api } from '../lib/api';
import { ruDate, ruDateTime, weekday } from '../lib/format';
import { errorText } from './ErrorToast';
import { UnsavedMark, useUnsavedChanges } from './UnsavedGuard';
import { Button, Segmented, SidePanel, StatusBadge, fieldCls, inputCls } from './ui';

type SortKey = 'days' | 'name' | 'last';
type Presence = 'all' | 'today' | 'yesterday' | string; // string — конкретная дата YYYY-MM-DD

const ROW_H = 44;
/** «Были на объекте» для периода, который ещё не начался: проходы за столько последних дней (как у сервера). */
const LOOKBACK_DAYS = 14;
/** Сколько ФИО показать в предпросмотре копирования, дальше — «и ещё N». */
const NAMES_SHOWN = 10;

type Tab = 'scud' | 'copy';

/** Почему человек пропущен при копировании (`skip[].reason`). */
const SKIP_REASON: Readonly<Record<string, string>> = { no_passes: 'нет проходов за период' };

function addDays(d: string, n: number) {
  return new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

/** «1 человек», «3 человека», «5 человек». */
export function people(n: number): string {
  const d10 = n % 10;
  const d100 = n % 100;
  const word = d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14) ? 'человека' : 'человек';
  return `${n} ${word}`;
}

/** ФИО списком: «Альфа Тестовый, Бета Тестовый и ещё 3». */
export function nameList(ids: readonly string[], nameOf: (id: string) => string): string {
  const shown = ids.slice(0, NAMES_SHOWN).map(nameOf).join(', ');
  return ids.length > NAMES_SHOWN ? `${shown} и ещё ${ids.length - NAMES_SHOWN}` : shown;
}

/**
 * «Добавить людей» — режим правой панели (003 FR-009, FR-033, FR-041, ВБ-38; 001 FR-017, FR-034; contracts/ui-panel.md,
 * «Режимы правой панели»). Два способа:
 * - «Были на объекте»: люди с проходами за период, поиск по ФИО и номеру СКУД, фильтр «Были», сортировка, массовый
 *   выбор. Период ещё не начался — проходы за последние 14 дней (сервер присылает таких кандидатов). Строки рисуются
 *   виртуально — 1000 человек прокручиваются так же быстро, как 100. Отмеченные люди — несохранённое: закрытие и
 *   переход спрашивают «Добавить выбранных?».
 * - «Из другого табеля»: выбор табеля-источника и предпросмотр до действия (`dryRun`): кто добавится, кто уже в табеле,
 *   кто пропущен и почему. Период ещё не начался — добавятся все, предпросмотр говорит об этом прямо.
 */
export function PeoplePanel({
  open,
  onClose,
  ts,
  tzOffset,
  busy,
  onAdd,
  onCopy,
  initialTab = 'scud',
}: {
  open: boolean;
  /** ✕ и Esc. Защиту несохранённого добавляет страница: `onClose={() => guard(close)}`. */
  onClose: () => void;
  ts: Timesheet;
  tzOffset: string;
  busy: boolean;
  /** Добавить отмеченных. Ошибка — исключение: выбор остаётся. */
  onAdd: (personIds: string[]) => Promise<void>;
  onCopy: (sourceId: number) => Promise<void>;
  /** С какой вкладки открыть: «Были на объекте» или «Из другого табеля». */
  initialTab?: Tab;
}) {
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState('');
  const [presence, setPresence] = useState<Presence>('all');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'days', desc: true });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  const [tab, setTab] = useState<Tab>('scud');
  const [source, setSource] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [copying, setCopying] = useState(false);

  const candidates = useQuery({
    queryKey: ['candidates', ts.id],
    queryFn: () => api.candidates(ts.id, ''),
    enabled: open,
  });
  const others = useQuery({ queryKey: ['timesheets', 'brigadier'], queryFn: api.list, enabled: open });
  const copyOptions: TimesheetListItem[] = (others.data ?? []).filter((t) => t.id !== ts.id);
  const preview = useQuery({
    queryKey: ['copy-preview', ts.id, source],
    queryFn: () => api.copyPreview(ts.id, source as number),
    enabled: open && tab === 'copy' && source !== null,
    retry: false,
  });
  // ФИО людей источника — для предпросмотра (сервер присылает номера).
  const sourceSheet = useQuery({
    queryKey: ['copy-source', source],
    queryFn: () => api.get(source as number),
    enabled: open && tab === 'copy' && source !== null,
    retry: false,
  });

  // Панель открылась — выбор и поиск с начала.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQ('');
    setActive(0);
    setAnchor(null);
    setTab(initialTab);
    setSource(null);
  }, [open, initialTab]);

  const add = async (ids: string[]) => {
    if (ids.length === 0) return;
    setAdding(true);
    try {
      await onAdd(ids);
      setSelected(new Set());
    } finally {
      setAdding(false);
    }
  };
  const latestAdd = useRef(add);
  latestAdd.current = add;

  const n = selected.size;
  useUnsavedChanges(
    open && n > 0
      ? {
          kind: 'people',
          summary: `Отмечено: ${people(n)}`,
          canSave: true,
          save: () => latestAdd.current([...selected]),
          discard: () => setSelected(new Set()),
          saveLabel: `Добавить ${n}`,
        }
      : null,
  );

  // Период ещё не начался: кандидаты — с проходами за последние 14 дней, дни полосы и фильтра — эти 14 дней.
  const future = ts.periodStart > ts.today;
  const days = future
    ? Array.from({ length: LOOKBACK_DAYS }, (_, i) => {
        const date = addDays(ts.today, i - (LOOKBACK_DAYS - 1));
        const wd = weekday(date);
        return { date, weekend: wd === 'сб' || wd === 'вс', future: false };
      })
    : ts.days.filter((d) => !d.future);
  const windowFrom = days[0]?.date ?? ts.periodStart;
  const presenceDate =
    presence === 'all'
      ? null
      : presence === 'today'
        ? ts.today
        : presence === 'yesterday'
          ? addDays(ts.today, -1)
          : presence;

  const rows = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('ru');
    const list = (candidates.data ?? []).filter(
      (c) =>
        (!needle || c.displayName.toLocaleLowerCase('ru').includes(needle) || c.personId.includes(needle)) &&
        (!presenceDate || c.presentDates.includes(presenceDate)),
    );
    const dir = sort.desc ? -1 : 1;
    const byName = (a: Candidate, b: Candidate) => a.displayName.localeCompare(b.displayName, 'ru');
    return [...list].sort((a, b) => {
      if (sort.key === 'name') return dir * byName(a, b);
      if (sort.key === 'last') return dir * a.lastPassAt.localeCompare(b.lastPassAt) || byName(a, b);
      return dir * (a.days - b.days) || byName(a, b);
    });
  }, [candidates.data, q, presenceDate, sort]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
    // До первого измерения (и в тестах) — высота списка в панели.
    initialRect: { width: 432, height: 480 },
  });

  useEffect(() => {
    if (active >= rows.length) setActive(Math.max(0, rows.length - 1));
  }, [rows.length, active]);

  function toggle(index: number, e?: MouseEvent | KeyboardEvent) {
    const row = rows[index];
    if (!row) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (e?.shiftKey && anchor !== null) {
        // Shift — выбрать диапазон от предыдущего щелчка.
        const [from, to] = anchor < index ? [anchor, index] : [index, anchor];
        for (let i = from; i <= to; i++) {
          const r = rows[i];
          if (r) next.add(r.personId);
        }
      } else if (next.has(row.personId)) next.delete(row.personId);
      else next.add(row.personId);
      return next;
    });
    setAnchor(index);
    setActive(index);
  }

  function onListKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(rows.length - 1, Math.max(0, active + (e.key === 'ArrowDown' ? 1 : -1)));
      setActive(next);
      virtualizer.scrollToIndex(next, { align: 'auto' });
    } else if (e.key === ' ') {
      e.preventDefault();
      toggle(active, e);
    } else if (e.key === 'Enter' && selected.size > 0) {
      e.preventDefault();
      void add([...selected]).catch(() => {});
    }
  }

  const allFoundSelected = rows.length > 0 && rows.every((r) => selected.has(r.personId));
  const sortHeader = (key: SortKey, label: string, cls: string) => (
    <button
      type="button"
      className={`${cls} flex items-center gap-1 font-medium hover:text-ink`}
      onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : key !== 'name' }))}
      aria-sort={sort.key === key ? (sort.desc ? 'descending' : 'ascending') : 'none'}
    >
      {label}
      {sort.key === key &&
        (sort.desc ? (
          <ArrowDown size={12} strokeWidth={2} aria-hidden />
        ) : (
          <ArrowUp size={12} strokeWidth={2} aria-hidden />
        ))}
    </button>
  );

  const plan: CopyRowsPreview | undefined = preview.data;
  const copyNow = async () => {
    if (source === null) return;
    setCopying(true);
    try {
      await onCopy(source);
    } finally {
      setCopying(false);
    }
  };

  const footer =
    tab === 'copy' ? (
      <>
        <span className="flex-1" aria-hidden />
        <Button
          variant="primary"
          icon={Copy}
          disabled={!plan || plan.add.length === 0 || busy || copying}
          onClick={() => void copyNow().catch(() => {})}
        >
          {plan ? `Скопировать состав (${plan.add.length})` : 'Скопировать состав'}
        </Button>
      </>
    ) : (
      <>
        <span className="text-[13px] whitespace-nowrap text-ink-2 tabular-nums">
          Выбрано <b className="font-semibold text-ink">{n}</b>
        </span>
        {n > 0 && <UnsavedMark />}
        <span className="flex-1" aria-hidden />
        {n > 0 && (
          <Button variant="ghost" onClick={() => setSelected(new Set())}>
            Снять выбор
          </Button>
        )}
        <Button
          variant="primary"
          icon={UserPlus}
          disabled={n === 0 || busy || adding}
          onClick={() => void add([...selected]).catch(() => {})}
        >
          Добавить {n || ''}
        </Button>
      </>
    );

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      label="Добавить людей"
      crumb={ts.brigadeName}
      title="Добавить людей"
      icon={UserPlus}
      footer={footer}
      initialFocus={searchRef}
    >
      <Segmented<Tab>
        label="Откуда"
        value={tab}
        onChange={setTab}
        options={[
          { value: 'scud', label: 'Были на объекте' },
          { value: 'copy', label: 'Из другого табеля' },
        ]}
        className="self-start max-sm:w-full"
      />
      {tab === 'copy' ? (
        <CopyFrom
          ts={ts}
          future={future}
          options={copyOptions}
          loading={others.isLoading}
          source={source}
          onSource={setSource}
          plan={plan}
          planLoading={preview.isFetching}
          planError={preview.error}
          nameOf={(id) =>
            sourceSheet.data?.rows.find((r) => r.personId === id)?.displayName ??
            ts.rows.find((r) => r.personId === id)?.displayName ??
            `№ ${id}`
          }
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <p className="text-[13px] text-ink-2">
            {future
              ? `Период ещё не начался — показаны люди с проходами за последние ${LOOKBACK_DAYS} дней (${ruDate(windowFrom).slice(0, 5)} — ${ruDate(ts.today).slice(0, 5)}). Уже добавленные не показаны.`
              : `Люди, которых СКУД видел на объекте с ${ruDate(ts.periodStart)} по ${ruDate(ts.periodEnd)}. Уже добавленные не показаны.`}
          </p>
          <div className="relative">
            <Search
              size={16}
              strokeWidth={1.75}
              className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
              aria-hidden
            />
            <input
              ref={searchRef}
              className={`${inputCls} pl-9`}
              placeholder="Фамилия или № СКУД"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  listRef.current?.focus();
                }
              }}
              aria-label="Поиск по фамилии или номеру СКУД"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[13px]">
            <select
              className={`${fieldCls} w-44 shrink-0 max-sm:w-full`}
              value={presence}
              onChange={(e) => setPresence(e.target.value)}
              aria-label="Были на объекте"
            >
              <option value="all">Были: весь период</option>
              <option value="today">Были сегодня</option>
              <option value="yesterday">Были вчера</option>
              {[...days].reverse().map((d) => (
                <option key={d.date} value={d.date}>
                  Были {ruDate(d.date).slice(0, 5)} ({weekday(d.date)})
                </option>
              ))}
            </select>
          </div>
          <div className="flex min-h-[220px] flex-1 flex-col overflow-hidden rounded-control border border-line">
            {/* Заголовок списка */}
            <div className="flex items-center gap-3 border-b border-line bg-subtle px-3 py-2 text-[12px] text-ink-3">
              <input
                type="checkbox"
                aria-label="Выбрать всех найденных"
                className="accent-accent"
                checked={allFoundSelected}
                onChange={() =>
                  setSelected((prev) => {
                    const next = new Set(prev);
                    for (const r of rows) {
                      if (allFoundSelected) next.delete(r.personId);
                      else next.add(r.personId);
                    }
                    return next;
                  })
                }
              />
              {sortHeader('name', 'ФИО', 'min-w-0 flex-1')}
              {sortHeader('days', 'Дней', 'w-10 justify-end')}
              <span className="w-24 font-medium max-sm:w-16" title="Присутствие по дням периода">
                {ruDate(ts.periodStart).slice(0, 5)} →
              </span>
            </div>

            {/* Список (виртуальный); role=listbox: множественный выбор, <select> здесь не подходит. */}
            <div
              ref={listRef}
              className="relative min-h-0 flex-1 overflow-y-auto focus:outline-none"
              tabIndex={0}
              role="listbox"
              aria-label="Люди с проходами за период"
              aria-multiselectable="true"
              aria-activedescendant={rows[active] ? `cand-${rows[active].personId}` : undefined}
              onKeyDown={onListKey}
            >
              {candidates.isLoading && <p className="p-4 text-[13px] text-ink-3">Загрузка…</p>}
              {!candidates.isLoading && rows.length === 0 && (
                <p className="p-4 text-[13px] text-ink-3">Никого не найдено.</p>
              )}
              <div style={{ height: virtualizer.getTotalSize() }} className="relative w-full">
                {virtualizer.getVirtualItems().map((v) => {
                  const c = rows[v.index];
                  if (!c) return null;
                  const isSel = selected.has(c.personId);
                  const present = new Set(c.presentDates);
                  const last = ruDateTime(c.lastPassAt, tzOffset);
                  return (
                    <div
                      key={c.personId}
                      id={`cand-${c.personId}`}
                      role="option"
                      aria-selected={isSel}
                      tabIndex={-1}
                      onClick={(e) => toggle(v.index, e)}
                      onKeyDown={() => {}}
                      className={`absolute top-0 left-0 flex w-full cursor-pointer items-center gap-3 border-b border-line px-3 text-[13px] select-none ${isSel ? 'bg-accent-soft' : 'hover:bg-subtle'} ${v.index === active ? 'shadow-[inset_2px_0_0_var(--color-accent)]' : ''}`}
                      style={{ height: ROW_H, transform: `translateY(${v.start}px)` }}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        readOnly
                        tabIndex={-1}
                        aria-hidden
                        className="accent-accent"
                      />
                      <span className="flex min-w-0 flex-1 flex-col leading-tight">
                        <span className="truncate">{c.displayName}</span>
                        <span className="truncate text-[11.5px] text-ink-3 tabular-nums">
                          № {c.personId} · посл. {last.slice(0, 5)} {last.slice(11)}
                        </span>
                      </span>
                      <span className="w-10 text-right font-medium tabular-nums">{c.days}</span>
                      <span
                        className="flex h-4 w-24 shrink-0 items-end gap-px max-sm:w-16"
                        aria-label={`Дней на объекте: ${c.days}`}
                      >
                        {days.map((d) => (
                          <span
                            key={d.date}
                            title={`${ruDate(d.date)}: ${present.has(d.date) ? 'был' : 'не был'}`}
                            className={`block flex-1 rounded-[1px] ${present.has(d.date) ? 'h-4 bg-ink-2' : d.weekend ? 'h-1.5 bg-weekend-strong/50' : 'h-1.5 bg-line'}`}
                          />
                        ))}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="border-t border-line px-3 py-1.5 text-[11.5px] text-ink-3 tabular-nums">
              Найдено {rows.length}
              {/* Подсказки клавиш на телефоне скрыты (contracts/ui-panel.md, «Узкий экран и телефон»). */}
              <span className="max-tablet:hidden">
                {' '}
                · ↑↓ строка · пробел отметить · Shift диапазон · Enter добавить
              </span>
            </p>
          </div>
        </div>
      )}
    </SidePanel>
  );
}

/**
 * «Из другого табеля»: табель-источник и предпросмотр до действия (FR-033, FR-041): сколько и кто добавится, кто уже
 * в табеле, кто пропущен и почему — с ФИО (T114). Период этого табеля ещё не начался — добавятся все, о чём сказано прямо.
 */
function CopyFrom({
  ts,
  future,
  options,
  loading,
  source,
  onSource,
  plan,
  planLoading,
  planError,
  nameOf,
}: {
  ts: Timesheet;
  future: boolean;
  options: readonly TimesheetListItem[];
  loading: boolean;
  source: number | null;
  onSource: (id: number) => void;
  plan: CopyRowsPreview | undefined;
  planLoading: boolean;
  planError: unknown;
  nameOf: (personId: string) => string;
}) {
  const reasons = new Map<string, string[]>();
  for (const x of plan?.skip ?? []) reasons.set(x.reason, [...(reasons.get(x.reason) ?? []), x.personId]);
  const planErrorText = planError ? errorText(planError, 'Предпросмотр не получен') : null;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <p className="text-[13px] text-ink-2">
        {future
          ? 'Период этого табеля ещё не начался — добавятся все люди выбранного табеля.'
          : `Добавятся люди выбранного табеля, у которых есть проходы за период этого табеля (${ruDate(ts.periodStart).slice(0, 5)} — ${ruDate(ts.periodEnd).slice(0, 5)}). Кто уже в табеле, не повторяется.`}
      </p>
      {loading ? (
        <p className="text-[13px] text-ink-3">Загрузка…</p>
      ) : options.length === 0 ? (
        <p className="rounded-control border border-line bg-subtle px-3 py-2.5 text-[13px] text-ink-2">
          Других табелей нет. Добавьте людей во вкладке «Были на объекте».
        </p>
      ) : (
        <div role="radiogroup" aria-label="Табель-источник" className="flex flex-col gap-1.5">
          {options.map((t) => {
            const on = t.id === source;
            return (
              <label
                key={t.id}
                className={`flex min-h-10 cursor-pointer items-center gap-3 rounded-control border px-3 py-2 text-[13px] transition-colors ${on ? 'border-accent bg-accent-soft' : 'border-line hover:bg-subtle'}`}
              >
                <input
                  type="radio"
                  name={`copy-src-${ts.id}`}
                  className="h-4 w-4 shrink-0 accent-accent"
                  checked={on}
                  onChange={() => onSource(t.id)}
                />
                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
                  <b className="font-semibold text-ink">{t.brigadeName}</b>
                  <span className="text-ink-2 tabular-nums">
                    {ruDate(t.periodStart).slice(0, 5)} — {ruDate(t.periodEnd).slice(0, 5)}
                  </span>
                  <StatusBadge status={t.status} />
                  {t.peopleCount !== undefined && (
                    <span className="text-ink-3 tabular-nums">{people(t.peopleCount)}</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}
      {source !== null && (
        <section
          aria-label="Что будет"
          aria-live="polite"
          className="rounded-control border border-line bg-subtle px-3.5 py-2.5 text-[13px] text-ink"
        >
          <h3 className="mb-1 text-[12px] font-medium text-ink-3">Что будет</h3>
          {plan ? (
            <ul className="flex flex-col gap-1">
              <li>
                Добавятся: <b className="font-semibold tabular-nums">{plan.add.length}</b>
                {plan.add.length > 0 ? ` — ${nameList(plan.add, nameOf)}` : ''}
              </li>
              {plan.have.length > 0 && (
                <li className="text-ink-2">
                  Уже в табеле: <span className="tabular-nums">{plan.have.length}</span> —{' '}
                  {nameList(plan.have, nameOf)}
                </li>
              )}
              {[...reasons].map(([reason, ids]) => (
                <li key={reason} className="text-ink-2">
                  Пропущены: <span className="tabular-nums">{ids.length}</span> —{' '}
                  {SKIP_REASON[reason] ?? reason}: {nameList(ids, nameOf)}
                </li>
              ))}
            </ul>
          ) : planErrorText ? (
            <p className="text-bad">{planErrorText}</p>
          ) : planLoading ? (
            <p className="text-ink-3">Считаем…</p>
          ) : null}
        </section>
      )}
    </div>
  );
}
